import React, { useState, useEffect } from 'react';
import { Save, Loader2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import api, { formatApiErrorDetail } from '../../lib/api';
import { useContent } from '../../context/ContentContext';
import { toast } from 'sonner';

export const ContentAdmin = () => {
  const [entries, setEntries] = useState(null);
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const { refresh } = useContent();

  useEffect(() => {
    api.get('/admin/content')
      .then((res) => {
        setEntries(res.data || []);
        const v = {};
        (res.data || []).forEach((e) => { v[e.key] = e.value; });
        setValues(v);
      })
      .catch(() => setEntries([]));
  }, []);

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => ({ ...prev, [key]: true }));
  };

  const handleUpload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/content-images', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleChange(key, `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success('Photo uploaded — click Save to publish it');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingKey(null);
    }
  };

  const dirtyCount = Object.keys(dirty).length;

  const handleSave = async () => {
    const updates = Object.keys(dirty).map((key) => ({ key, value: values[key] }));
    if (!updates.length) return;
    setSaving(true);
    try {
      await api.put('/admin/content', { updates });
      toast.success(`Saved ${updates.length} change${updates.length > 1 ? 's' : ''} — live on the site now`);
      setDirty({});
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (entries === null) {
    return <p className="text-[#5f6d51]/60 py-8 text-center">Loading…</p>;
  }

  const pages = [];
  const grouped = {};
  entries.forEach((e) => {
    if (!grouped[e.page]) { grouped[e.page] = []; pages.push(e.page); }
    grouped[e.page].push(e);
  });

  const renderField = (entry) => {
    if (entry.type === 'textarea') {
      return (
        <Textarea
          id={`content-${entry.key}`}
          value={values[entry.key] ?? ''}
          onChange={(e) => handleChange(entry.key, e.target.value)}
          rows={3}
          data-testid={`content-field-${entry.key}`}
          className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
        />
      );
    }
    if (entry.type === 'image') {
      return (
        <div className="mt-2 flex flex-col sm:flex-row gap-4 items-start">
          {values[entry.key] && (
            <img
              src={values[entry.key]}
              alt={entry.label}
              className="w-32 h-24 object-cover rounded-lg border border-[#bd8cae]/40 flex-shrink-0"
              data-testid={`content-preview-${entry.key}`}
            />
          )}
          <div className="flex-1 w-full space-y-2">
            <Input
              id={`content-${entry.key}`}
              value={values[entry.key] ?? ''}
              onChange={(e) => handleChange(entry.key, e.target.value)}
              placeholder="Paste an image URL…"
              data-testid={`content-field-${entry.key}`}
              className="border-[#bd8cae]/50 focus:border-[#5d6b3c]"
            />
            <label className="inline-flex items-center gap-2 text-sm text-[#5d6b3c] border border-[#5d6b3c]/40 rounded-md px-3 py-1.5 cursor-pointer hover:bg-[#5d6b3c]/10 transition-colors">
              <Upload className="w-4 h-4" />
              {uploadingKey === entry.key ? 'Uploading…' : 'Upload New Photo'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                disabled={uploadingKey === entry.key}
                data-testid={`content-upload-${entry.key}`}
                onChange={(e) => handleUpload(entry.key, e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
      );
    }
    return (
      <Input
        id={`content-${entry.key}`}
        value={values[entry.key] ?? ''}
        onChange={(e) => handleChange(entry.key, e.target.value)}
        data-testid={`content-field-${entry.key}`}
        className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
      />
    );
  };

  return (
    <div data-testid="admin-content-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-xl text-[#3c4a2a]">Page Content</h2>
          <p className="text-sm text-[#5f6d51]/70 mt-1">
            Edit the text that appears across the website — no coding needed. Changes go live as soon as you save.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || dirtyCount === 0}
          data-testid="content-save-btn"
          className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white flex-shrink-0"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {dirtyCount > 0 ? `Save ${dirtyCount} Change${dirtyCount > 1 ? 's' : ''}` : 'All Saved'}
        </Button>
      </div>

      <Accordion type="multiple" className="w-full">
        {pages.map((page) => (
          <AccordionItem key={page} value={page} className="border-b border-[#bd8cae]/30">
            <AccordionTrigger
              className="text-[#3c4a2a] hover:text-[#5d6b3c] font-medium"
              data-testid={`content-page-${page.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <span className="flex-1 text-left">{page}</span>
              <span className="mr-3 text-xs text-[#5f6d51]/60 font-normal">{grouped[page].length} fields</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-5 pt-2 pb-4">
                {grouped[page].map((entry) => (
                  <div key={entry.key}>
                    <Label htmlFor={`content-${entry.key}`} className="text-[#3c4a2a] text-sm">
                      {entry.label}
                      {dirty[entry.key] && (
                        <span className="ml-2 text-xs text-[#bd8cae] font-medium">• unsaved</span>
                      )}
                    </Label>
                    {renderField(entry)}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};
